import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import { notificationService } from '../services/notificationService';

const ConsultantNotifications = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRec, setSelectedRec] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleNewRecommendation = (newRec) => {
    // Show notification toast
    setNotification({
      type: 'new',
      message: `New AI recommendation generated for ${newRec?.client_name || 'client'}`,
      timestamp: new Date()
    });

    // Add to list if pending review
    if (newRec?.status === 'pending_review') {
      setRecommendations(prev => [{
        id: newRec?.id,
        clientId: newRec?.client_id,
        clientName: newRec?.client_name,
        clientEmail: newRec?.client_email,
        clientCompany: newRec?.client_company,
        serviceName: newRec?.service_name,
        category: newRec?.category,
        rationale: newRec?.rationale,
        predictedROI: newRec?.predicted_roi,
        roiConfidence: newRec?.roi_confidence,
        implementationMonths: newRec?.implementation_months,
        costRange: newRec?.cost_range,
        successMetrics: newRec?.success_metrics,
        riskFactors: newRec?.risk_factors,
        status: newRec?.status,
        aiAnalysis: newRec?.ai_analysis,
        createdAt: newRec?.created_at
      }, ...prev]);
    }

    // Clear notification after 5 seconds
    setTimeout(() => setNotification(null), 5000);
  };

  const handleUpdatedRecommendation = (updated, old) => {
    // Remove from pending list if status changed
    if (updated?.status !== 'pending_review') {
      setRecommendations(prev => prev?.filter(r => r?.id !== updated?.id));
      
      setNotification({
        type: 'update',
        message: `Recommendation ${updated?.status?.replace('_', ' ')}`,
        timestamp: new Date()
      });

      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleDeletedRecommendation = (deleted) => {
    setRecommendations(prev => prev?.filter(r => r?.id !== deleted?.id));
  };

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    // Check if user is admin/consultant
    if (userProfile?.role !== 'admin' && userProfile?.role !== 'manager') {
      navigate('/dashboard');
      return;
    }

    loadPendingRecommendations();

    // Subscribe to real-time updates
    const channel = notificationService?.subscribeToRecommendations(
      handleNewRecommendation,
      handleUpdatedRecommendation,
      handleDeletedRecommendation
    );

    return () => {
      notificationService?.unsubscribe(channel);
    };
  }, [user, userProfile, navigate]);

  const loadPendingRecommendations = async () => {
    try {
      setLoading(true);
      const data = await notificationService?.getPendingRecommendations();
      setRecommendations(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (recId) => {
    try {
      setProcessing(true);
      await notificationService?.updateRecommendationStatus(
        recId,
        'approved',
        reviewNotes || 'Approved for client presentation'
      );
      setSelectedRec(null);
      setReviewNotes('');
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to approve recommendation');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (recId) => {
    try {
      setProcessing(true);
      await notificationService?.updateRecommendationStatus(
        recId,
        'rejected',
        reviewNotes || 'Requires revision'
      );
      setSelectedRec(null);
      setReviewNotes('');
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to reject recommendation');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'implemented': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Consultant Notifications - AI Recommendations Review | Northstar Analytics</title>
      </Helmet>
      <Header />
      {/* Real-time notification toast */}
      {notification && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-lg shadow-lg p-4 max-w-sm ${
            notification?.type === 'new' ? 'bg-blue-600' : 'bg-green-600'
          } text-white`}>
            <div className="flex items-start gap-3">
              <Icon name="bell" className="w-5 h-5 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{notification?.message}</p>
                <p className="text-xs opacity-90 mt-1">
                  {notification?.timestamp?.toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Recommendations Review</h1>
              <p className="text-gray-600 mt-2">
                Review and approve OpenAI-generated client recommendations in real-time
              </p>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-700">Live Updates Active</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <Icon name="alert-circle" className="w-5 h-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Review</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{recommendations?.length || 0}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Icon name="clock" className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg ROI Confidence</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {recommendations?.length > 0
                    ? Math.round(
                        recommendations?.reduce((sum, r) => sum + r?.roiConfidence, 0) /
                        recommendations?.length
                      )
                    : 0}%
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Icon name="trending-up" className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Predicted ROI</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {recommendations?.length > 0
                    ? Math.round(
                        recommendations?.reduce((sum, r) => sum + r?.predictedROI, 0) /
                        recommendations?.length
                      )
                    : 0}%
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Icon name="dollar-sign" className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations List */}
        {recommendations?.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Icon name="check-circle" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">
              No pending recommendations to review. New recommendations will appear here in real-time.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {recommendations?.map((rec) => (
              <div key={rec?.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{rec?.serviceName}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          getStatusBadgeColor(rec?.status)
                        }`}>
                          {rec?.status?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Icon name="user" className="w-4 h-4" />
                          {rec?.clientName || rec?.clientEmail}
                        </span>
                        {rec?.clientCompany && (
                          <span className="flex items-center gap-1">
                            <Icon name="building" className="w-4 h-4" />
                            {rec?.clientCompany}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Icon name="clock" className="w-4 h-4" />
                          {new Date(rec.createdAt)?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="mb-4">
                    <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-medium">
                      <Icon name="tag" className="w-4 h-4" />
                      {rec?.category}
                    </span>
                  </div>

                  {/* ROI Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Predicted ROI</p>
                      <p className="text-2xl font-bold text-green-600">{rec?.predictedROI}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Confidence Level</p>
                      <p className="text-2xl font-bold text-blue-600">{rec?.roiConfidence}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Timeline</p>
                      <p className="text-2xl font-bold text-purple-600">{rec?.implementationMonths}mo</p>
                    </div>
                  </div>

                  {/* Rationale */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">AI Analysis & Rationale</h4>
                    <p className="text-gray-700 leading-relaxed">{rec?.rationale}</p>
                  </div>

                  {/* Cost Range */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Investment Range</h4>
                    <p className="text-gray-700 font-medium">{rec?.costRange}</p>
                  </div>

                  {/* Success Metrics */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Success Metrics</h4>
                    <ul className="space-y-2">
                      {rec?.successMetrics?.map((metric, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <Icon name="check" className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{metric}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Risk Factors */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Risk Factors</h4>
                    <ul className="space-y-2">
                      {rec?.riskFactors?.map((risk, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <Icon name="alert-triangle" className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span>{risk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  {selectedRec?.id === rec?.id ? (
                    <div className="border-t pt-4">
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e?.target?.value)}
                        placeholder="Add review notes (optional)..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                        rows={3}
                      />
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={() => handleApprove(rec?.id)}
                          disabled={processing}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          {processing ? 'Processing...' : 'Approve Recommendation'}
                        </Button>
                        <Button
                          onClick={() => handleReject(rec?.id)}
                          disabled={processing}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        >
                          {processing ? 'Processing...' : 'Reject & Request Revision'}
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedRec(null);
                            setReviewNotes('');
                          }}
                          disabled={processing}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 border-t pt-4">
                      <Button
                        onClick={() => setSelectedRec(rec)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Review Recommendation
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ConsultantNotifications;